import {LineType} from "@luciad/ria/geodesy/LineType.js";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {FeatureModel} from "@luciad/ria/model/feature/FeatureModel.js";
import {MemoryStore} from "@luciad/ria/model/store/MemoryStore.js";
import {WMSTileSetModel} from "@luciad/ria/model/tileset/WMSTileSetModel.js";
import {CoordinateReference} from "@luciad/ria/reference/CoordinateReference.js";
import {getReference} from "@luciad/ria/reference/ReferenceProvider.js";
import {Bounds} from "@luciad/ria/shape/Bounds.js";
import {LonLatPointFormat} from "@luciad/ria/shape/format/LonLatPointFormat.js";
import {Point} from "@luciad/ria/shape/Point.js";
import {createBounds as riaCreateBounds, createPoint, createPolygon} from "@luciad/ria/shape/ShapeFactory.js";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer.js";
import {FeaturePainter} from "@luciad/ria/view/feature/FeaturePainter.js";
import {GridLayer} from "@luciad/ria/view/grid/GridLayer.js";
import {LonLatGrid} from "@luciad/ria/view/grid/LonLatGrid.js";
import {Layer} from "@luciad/ria/view/Layer.js";
import {Map} from "@luciad/ria/view/Map.js";
import {HatchedImageBuilder} from "@luciad/ria/view/style/HatchedImageBuilder.js";
import {WMSTileSetLayer} from "@luciad/ria/view/tileset/WMSTileSetLayer.js";
import {Bounds as ReactorBounds, SpatialModel} from "../../../model";
import {BackgroundMetadata} from "./model";
import {RasterTileSetLayer} from "@luciad/ria/view/tileset/RasterTileSetLayer.js";
import {UrlTileSetModel} from "@luciad/ria/model/tileset/UrlTileSetModel.js";
import {oidcProviderInfo} from "../../../oidc/startOidcProvider";
import {Logger} from "../../util/Logger";
import {oidcUtils} from "../../../oidc/utils/OidcUtils";
import {WMSVersion} from "@luciad/ria/ogc/WMSVersion.js";
import {localRepository, WMS_BROWSER_CACHE_VERSION} from "../../LocalRepository";

const LLH_REF = getReference("EPSG:4326");
const WEB_MERCATOR = getReference("EPSG:3857");
const WEB_MERCATOR_MAX_LAT = 85.06;

export function createMap(mapNode, shouldAddGridLayer, restrictNavigationBounds, reference,
                          background: BackgroundMetadata, previewBackgroundPath, backgroundDataName,
                          previewBackgroundColor) {
    let newReference = null;
    if (background?.crs && background?.crs !== "") {
        try {
            newReference = getReference(background.crs);
        } catch (e) {
            Logger.getLogger("RiaApi").error(`Invalid Map Preview CRS Override (${background.crs})`);
            newReference = null;
        }
    }
    const map = new Map(mapNode, {reference: newReference || reference || WEB_MERCATOR});
    map.layerTree.addChild(
        createBackgroundLayer(map, previewBackgroundPath, backgroundDataName, background, previewBackgroundColor));
    map.layerTree.addChild(createAreaOfInterestLayer());

    if (shouldAddGridLayer) {
        const gridLayer = createGridLayer();
        map.layerTree.addChild(gridLayer);
    }

    if (restrictNavigationBounds) {
        map.mapNavigator.constraints = {
            limitBounds: {
                bounds: createBounds(restrictNavigationBounds),
            },
        };
    }

    map.mapNavigator.defaults.snapToScaleLevels = false;

    // Prevent user from zooming in past the size of a normal residential house
    map.maxMapScale = [1 / 100, 1 / 100];

    return map;
}

export function createFeature(coordinates: number[]): Feature {
    if (coordinates?.length > 5) {
        const points: Point[] = [];
        for (let i = 0; i < coordinates.length; i += 2) {
            points.push(createPoint(LLH_REF, [coordinates[i], coordinates[i + 1]]));
        }
        const shape = createPolygon(LLH_REF, points);
        return new Feature(shape, {}, DRAW_ID);
    } else {
        return null;
    }
}

export function destroyMap(map) {
    if (map) {
        map.destroy();
    }
}

function createGridLayer() {
    const settings = [];
    settings.push({scale: 2.0E-8 * 4, deltaLon: 1, deltaLat: 1});
    settings.push({scale: 2.0E-8, deltaLon: 5, deltaLat: 5});
    settings.push({scale: 9.0E-9, deltaLon: 10, deltaLat: 10});
    settings.push({scale: 5.0E-9, deltaLon: 20, deltaLat: 20});
    settings.push({scale: 0, deltaLon: 45, deltaLat: 45});

    const grid = new LonLatGrid(settings);
    //Set the default styling for grid lines and labels
    grid.fallbackStyle = {
        labelFormat: new LonLatPointFormat({pattern: "lat(+DM),lon(+DM)"}),
        originLabelFormat: new LonLatPointFormat({pattern: "lat(+D),lon(+D)"}),
        originLineStyle: {
            color: "rgba(230, 20, 20, 0.6)",
            width: 1,
        },
        lineStyle: {
            color: "rgba(210,210,210,0.4)",
            width: 0.5,
        },
        originLabelStyle: {
            fill: "rgba(210,210,210,0.8)",
            halo: "rgba(230, 20, 20, 0.8)",
            haloWidth: 3,
            font: "12px sans-serif",
        },
        labelStyle: {
            fill: "rgb(220,220,220)",
            halo: "rgb(102,102,102)",
            haloWidth: 3,
            font: "12px sans-serif",
        },
    };

    return new GridLayer(grid, {label: "Grid"});
}

/**
 * Creates a tiled WMS model.
 * V180-1067: To avoid caching on the client side, timestamp was added as a parameter to the requests.
 */
function createWMSModel(url: string, layers: string[], reference: CoordinateReference,
                        backgroundColor: string): WMSTileSetModel {
    const basicOptions = {
        getMapRoot: url,
        version: WMSVersion.V130,
        reference,
        layers,
        transparent: true,
        backgroundColor
    };
    const cacheVersion = localRepository.getParameter(WMS_BROWSER_CACHE_VERSION);
    if (cacheVersion) {
        basicOptions["requestParameters"] = {
            v: cacheVersion,
        };
    }

    if (oidcProviderInfo?.enabled) {
        const authOptions = oidcUtils.getWmsOptions();
        const mergedOptions = {
            ...basicOptions,
            ...authOptions
        };
        return new WMSTileSetModel(mergedOptions);
    } else {
        return new WMSTileSetModel(basicOptions);
    }
}

function createWMSModelWithStyle(url: string, layers: string[], reference: CoordinateReference, wmsStyle: string): WMSTileSetModel {
    const basicOptions = {
        getMapRoot: url,
        version: WMSVersion.V130,
        reference,
        layers,
        transparent: true,
        styles: [wmsStyle],
    };
    const cacheVersion = localRepository.getParameter(WMS_BROWSER_CACHE_VERSION);
    if (cacheVersion) {
        basicOptions["requestParameters"] = {
            v: cacheVersion,
        };
    }

    if (oidcProviderInfo?.enabled) {
        const authOptions = oidcUtils.getWmsOptions();
        const mergedOptions = {
            ...basicOptions,
            ...authOptions
        };
        return new WMSTileSetModel(mergedOptions);
    } else {
        return new WMSTileSetModel(basicOptions);
    }
}

const AREA_OF_INTEREST_LAYER = "area-of-interest";
const AREA_OF_INTEREST_ID = "aoi-id";

const DRAW_LAYER = "draw";
export const DRAW_ID = "draw-id";
const DRAW_FILL_COLOR = "rgba(65,105,225,0.9)";
const DRAW_LINE_COLOR = "#FF0000";

function createAreaOfInterestPainter() {
    const color = "rgba(255,40,40,0.8)";
    const width = 2;
    const style = {
        stroke: {color, width},
        fill: {
            image: new HatchedImageBuilder().patterns([HatchedImageBuilder.Pattern.SLASH])
                .patternSize(8, 8)
                .lineColor(color)
                .lineWidth(width)
                .backgroundColor("rgba(0,0,0,0)")
                .build(),
        },
    };
    const painter = new FeaturePainter();
    painter.paintBody = (geoCanvas, feature, shape/*, layer, map, paintState*/) => {
        geoCanvas.drawShape(shape, style);
    };
    return painter;
}

function createAreaOfInterestLayer() {
    const model = new FeatureModel(new MemoryStore());
    const painter = createAreaOfInterestPainter();

    return new FeatureLayer(model, {
        id: AREA_OF_INTEREST_LAYER,
        selectable: false,
        editable: false,
        painter,
    });
}

export function createDrawLayer(drawFillColor?: string) {
    const model = new FeatureModel(new MemoryStore());

    const painter = new FeaturePainter();
    painter.paintBody = (geoCanvas, feature, shape, layer, map, paintState): void => {
        geoCanvas.drawShape(shape, {
            lineType: LineType.CONSTANT_BEARING,
            stroke: {
                color: DRAW_LINE_COLOR,
                width: 1.5
            },
            fill: {
                color: drawFillColor || DRAW_FILL_COLOR
            }
        });
    };

    return new FeatureLayer(model, {
        id: DRAW_LAYER,
        selectable: true,
        editable: true,
        painter,
    });
}

export function setAreaOfInterest(map, aoiBounds) {
    const aoiLayer = map.layerTree.findLayerById(AREA_OF_INTEREST_LAYER);
    aoiLayer.model.remove(AREA_OF_INTEREST_ID);

    if (aoiBounds) {
        aoiLayer.model.add(new Feature(aoiBounds, {}, AREA_OF_INTEREST_ID));
    }
}

export function addWMSLayerToMap(map, wmsUrl, layerName, layerTitle, riaBounds, layerReference,
                                 backgroundColor: string) {
    const wmsLayer = createWMSLayer(wmsUrl, layerName, layerTitle, layerReference, backgroundColor) as any;
    wmsLayer._internalBounds = riaBounds;
    map.layerTree.addChild(wmsLayer);
    return wmsLayer;
}

export function addWMSLayerToMapWithStyle(map, wmsUrl, layerName, layerTitle, riaBounds, layerReference, wmsStyle: string) {
    const wmsLayer = createWMSLayerWithStyle(wmsUrl, layerName, layerTitle, layerReference, wmsStyle) as any;
    wmsLayer._internalBounds = riaBounds;
    map.layerTree.addChild(wmsLayer);
    return wmsLayer;
}

export function fitOnBounds(map, reactorBounds: ReactorBounds[], animatedFit) {
    if (reactorBounds) {
        const riaBounds = createUnionBounds(reactorBounds);
        const animationOptions = animatedFit ? {duration: 1500} : false;
        map.mapNavigator.fit({bounds: riaBounds, animate: animationOptions});
    }
}

function createTileLayer(url: string, reference, subdomains?: string[]) {
    let bounds = reference.bounds;
    if (getReference("EPSG:3857").equals(reference)) {
        // Patch for "EPSG:3857".
        bounds = riaCreateBounds(reference, [-20037508.34278924, 40075016.68557848, -20037508.3520, 40075016.7040]);
    }

    return new RasterTileSetLayer(new UrlTileSetModel({
        baseURL: url,
        subdomains,
        reference,
        bounds,
    }));
}


function createWMSLayerWithStyle(url: string, layerName: string, layerLabel: string,
                                reference: CoordinateReference, wmsStyle: string): WMSTileSetLayer {
    return new WMSTileSetLayer(
        createWMSModelWithStyle(url, [layerName], reference, wmsStyle), {
            label: layerLabel || layerName,
            visible: true,
        },
    );
}
function createWMSLayer(url: string, layerName: string, layerLabel: string,
                        reference: CoordinateReference, backgroundColor: string): WMSTileSetLayer {
    return new WMSTileSetLayer(
        createWMSModel(url, [layerName], reference, backgroundColor), {
            label: layerLabel || layerName,
            visible: true,
        },
    );
}

function createBackgroundLayer(map: Map, previewBackgroundPath: string, backgroundDataName: string,
                               background: BackgroundMetadata, backgroundColor: string): Layer {
    if (!background || background.type === "local") {
      return createWMSLayer(previewBackgroundPath, backgroundDataName, "Background",
          map.reference, backgroundColor);
    }

    switch (background.type) {
        case "wms":
            return createWMSLayer(background.url, background.layerName, "background", map.reference, backgroundColor);
        case "urlTileSet":
            return createTileLayer(background.url, map.reference, background.subdomains);
    }

}

export function createBounds(reactorBounds: ReactorBounds): Bounds {
    return riaCreateBounds(LLH_REF,
        [reactorBounds.x, reactorBounds.width, reactorBounds.y, reactorBounds.height]);
}

export function createUnionBounds(reactorBoundsArray: ReactorBounds[]): Bounds {
    let unionBounds: Bounds = null;
    reactorBoundsArray.forEach((reactorBound) => {
        const riaBound = createBounds(reactorBound);
        if (unionBounds) {
            unionBounds.setTo2DUnion(riaBound);
        } else {
            unionBounds = riaBound;
        }
    });

    // normalize the lat to web mercator min/max
    if (unionBounds.y < -WEB_MERCATOR_MAX_LAT) {
        unionBounds.setTo2D(unionBounds.x, unionBounds.width, -WEB_MERCATOR_MAX_LAT, (unionBounds.y + unionBounds.height) + WEB_MERCATOR_MAX_LAT);
    }
    if ((unionBounds.y + unionBounds.height) > WEB_MERCATOR_MAX_LAT) {
        unionBounds.setTo2D(unionBounds.x, unionBounds.width, unionBounds.y, WEB_MERCATOR_MAX_LAT - unionBounds.y);
    }
    return unionBounds;
}

export function getBounds(dataElements: SpatialModel[]): Bounds {
    if (dataElements == null) {
        return null;
    }
    const reactorBounds: ReactorBounds[] = dataElements.map((element) => {
        return element.wgs84Bounds;
    });
    return reactorBounds.length ? createUnionBounds(reactorBounds) :
        createBounds({x: -180, y: -WEB_MERCATOR_MAX_LAT, width: 360, height: 2 * WEB_MERCATOR_MAX_LAT});
}